import React from "react";
import ColorSelector from "./ColorSelector";
import Configurator from "../configurator/Configurator";
import {ConfiguredElement, ProductData} from "../../providers/product/product-types";
import {Range} from "../inputs/Range";
import ProductViewer, {ElementConfig} from "./ProductViewer";
import {useTranslation} from "react-i18next";
import {ProductImageGrid} from "./ProductImageGrid";
import {ProductNameContainer} from "./ProductNameContainer";
import {ProductDetails} from "./ProductDetails";
import {Color} from "../../providers/types";
import {ProductExamples} from "./ProductExamples";

export interface ProductProps {
    product: ProductData;
}

export interface ConfigChangeEvent {
    index?: number;
    newConfig?: ConfiguredElement;
}

const Product = ({product}: ProductProps) => {
    const {t} = useTranslation();
    const [config, setConfig] = React.useState<ConfiguredElement[]>(product.default);
    const [color, setColor] = React.useState<Color | null>(null);
    const [radius, setRadius] = React.useState<number>(90);
    const putToCart = {
        mutate: () => {
        }
    }
    const [elements, setElements] = React.useState<ElementConfig[]>([]);
    const bottom = React.useMemo(() => {
        if (!product.bottomElement) {
            return null;
        }
        const isRoundedBottom = config[config.length - 1].modifiers.find(m => m.name === 'ROUNDED_BOTTOM');
        return product.bottomElement(isRoundedBottom ? radius - 10 : radius);
    }, [radius, config]);

    React.useEffect(() => {
        console.log('Planter changed!')
        setConfig(product.default);
        setElements(product.default.map(config => createElementFromConfig(config)));
    }, [product]);

    const onConfigChange = (ev: ConfigChangeEvent) => {
        if (ev.index !== undefined && !ev.newConfig) {
            setConfig(prev => prev.filter((_, i) => i !== ev.index));
            setElements(prev => prev.filter((_, i) => i !== ev.index));
            return;
        }

        if (ev.index === undefined) {
            setConfig(prev => ([...prev, ev.newConfig!]));
            setElements(prev => ([...prev, createElementFromConfig(ev.newConfig!)]))
            return;
        }

        setConfig(prev => {
            const newConfig = [...prev];
            newConfig[ev.index as number] = ev.newConfig!;
            return newConfig;
        })
        setElements(prev => {
            const newConfig = [...prev];
            newConfig[ev.index as number] = createElementFromConfig(ev.newConfig!);
            return newConfig;
        })

    }

    const createElementFromConfig = (config: ConfiguredElement, r = radius) => {
        console.time(config.config.title);
        const tmpGeometry = config.config.generatorFunction(r, config.values);
        const geometry = config.modifiers.reduce((acc, mod) => mod.function.apply(acc, config.values), tmpGeometry);
        console.timeEnd(config.config.title)
        return {
            height: config.values.height,
            geometry,
        }
    }

    const reCalculateElements = (newRadius: number) => {
        setRadius(newRadius);
        setElements(config.map(config => createElementFromConfig(config, newRadius)));
    }

    return (
        <>
            <ProductExamples/>
            <main className="mx-auto mt-8 max-w-2xl px-4 pb-16 sm:px-6 sm:pb-24 lg:max-w-7xl lg:px-8">
                <div className="lg:grid lg:auto-rows-min lg:grid-cols-12 lg:gap-x-8">
                    <ProductNameContainer product={product}/>

                    <div className="mt-8 lg:col-span-7 lg:col-start-1 lg:row-span-3 lg:row-start-1 lg:mt-0">
                        <Range title={t('CONFIG.WIDTH')} value={radius} onChange={reCalculateElements} step={0.5}
                               min={30}
                               max={125}
                               unit={'cm'} valMapper={val => val / 5}/>
                        <ProductViewer bottom={bottom} elements={elements} color={color?.code || 'blue'}/>
                        <ProductImageGrid product={product}/>
                    </div>

                    <div className="mt-8 lg:col-span-5">
                        <form>
                            <ColorSelector selectedColor={color} setSelectedColor={setColor}/>
                            {/*<VariantSelector product={product} selectedVariant={variant} setSelectedVariant={setVariant}/>*/}
                            <Configurator product={product} config={config} onConfigChange={onConfigChange}/>
                            <button
                                onClick={() => putToCart.mutate()}
                                type="button"
                                className="mt-8 flex w-full items-center justify-center rounded-md border border-transparent bg-yellow-400 px-8 py-3 text-center font-medium transition duration-300 text-gray-700 hover:bg-yellow-500"
                            >
                                {t('BUTTON.ADD_TO_CART')}
                            </button>
                        </form>

                        <ProductDetails product={product}/>
                    </div>
                </div>
            </main>
        </>
    );
}

export default Product;